import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  EmailFooterProfile,
  renderEmailBuilderState,
} from "./emailBuilderData";
import { EmailFormState } from "./emailMockData";

export type EmailPreviewFrameProps = {
  html: string;
  title: string;
  containerClassName: string;
  iframeClassName: string;
  fitToContainer?: boolean;
  scrolling?: "auto" | "no" | "yes";
};

export type ResolveEmailPreviewScaleInput = {
  availableWidth: number;
  logicalWidth?: number;
};

export const EMAIL_PREVIEW_LOGICAL_WIDTH = 640;

export const resolveEmailPreviewScale = ({
  availableWidth,
  logicalWidth = EMAIL_PREVIEW_LOGICAL_WIDTH,
}: ResolveEmailPreviewScaleInput): number => {
  if (availableWidth <= 0 || logicalWidth <= 0) {
    return 1;
  }

  return Math.min(1, availableWidth / logicalWidth);
};

export const EmailPreviewFrame = ({
  html,
  title,
  containerClassName,
  iframeClassName,
  fitToContainer = false,
  scrolling = "auto",
}: EmailPreviewFrameProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);

  useLayoutEffect(() => {
    if (!fitToContainer) {
      return undefined;
    }

    const measureAvailableWidth = () => {
      const nextAvailableWidth = containerRef.current?.clientWidth || 0;
      setAvailableWidth((currentWidth) =>
        currentWidth === nextAvailableWidth ? currentWidth : nextAvailableWidth,
      );
    };

    measureAvailableWidth();
    window.addEventListener("resize", measureAvailableWidth);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measureAvailableWidth);
    if (containerRef.current) {
      resizeObserver?.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", measureAvailableWidth);
      resizeObserver?.disconnect();
    };
  }, [fitToContainer]);

  const scale = resolveEmailPreviewScale({ availableWidth });
  const fitStyle = fitToContainer
    ? {
        height: `${100 / scale}%`,
        left: 0,
        maxWidth: "none",
        position: "absolute" as const,
        top: 0,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        width: `${EMAIL_PREVIEW_LOGICAL_WIDTH}px`,
      }
    : undefined;

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      data-testid="email-preview-frame"
      data-preview-scale={fitToContainer ? scale : undefined}
      style={fitToContainer ? { position: "relative" } : undefined}
    >
      <iframe
        srcDoc={html}
        title={title}
        scrolling={scrolling}
        sandbox=""
        className={iframeClassName}
        style={fitStyle}
      />
    </div>
  );
};

export type EmailPreviewDialogProps = {
  open: boolean;
  email: EmailFormState | null;
  persistedHtml?: string | null;
  loading: boolean;
  error?: string;
  footerProfile?: EmailFooterProfile | null;
  onClose: () => void;
};

export const EmailPreviewDialog = ({
  open,
  email,
  persistedHtml = null,
  loading,
  error = "",
  footerProfile,
  onClose,
}: EmailPreviewDialogProps) => {
  const renderedEmailHtml = useMemo(
    () =>
      persistedHtml?.trim()
        ? persistedHtml
        : email
          ? renderEmailBuilderState(
              email.builderState,
              footerProfile || undefined,
            ).html
          : "",
    [email, footerProfile, persistedHtml],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="email-preview-dialog-title"
      PaperProps={{
        sx: {
          width: "min(640px, 92vw)",
          maxWidth: "none",
          margin: 0,
          height: "min(90vh, 920px)",
          borderRadius: "14px",
          fontFamily: "'DM Sans', sans-serif",
          "& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiIconButton-root, & .MuiAlert-root":
            { fontFamily: "inherit" },
        },
      }}
    >
      <DialogTitle
        id="email-preview-dialog-title"
        className="flex items-start justify-between gap-4 border-b border-gray-200"
        sx={{ padding: "20px 26px" }}
      >
        <span className="min-w-0 break-words pr-5">
          <span className="block text-[17px] font-semibold text-gray-950">
            Email Preview
          </span>
          {email ? (
            <span className="mt-0.5 block text-[14px] font-normal text-[#727272]">
              Subject: {email.subject || "Untitled communication"}
            </span>
          ) : null}
        </span>
        <IconButton
          type="button"
          onClick={onClose}
          aria-label="Close email preview"
          sx={{
            width: 28,
            height: 28,
            position: "absolute",
            top: 14,
            right: 14,
            padding: 0,
            flexShrink: 0,
            borderRadius: "50%",
            backgroundColor: "#F21B1B",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#DC2626" },
          }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#F0F0F1",
          padding: "24px",
          "&.MuiDialogContent-root": { paddingTop: "24px" },
        }}
      >
        {loading ? (
          <div className="flex h-full min-h-[360px] items-center justify-center gap-3 text-[15px] font-semibold text-gray-600">
            <CircularProgress size={24} />
            Loading saved communication…
          </div>
        ) : error ? (
          <Alert severity="error" className="mt-4">
            {error}
          </Alert>
        ) : email ? (
          <EmailPreviewFrame
            html={renderedEmailHtml}
            title={`Email preview: ${email.subject || email.campaignName || "Untitled communication"}`}
            scrolling="auto"
            fitToContainer
            containerClassName="mx-auto h-full min-h-[560px] w-full max-w-[520px] overflow-hidden rounded-lg border border-[#ECECEC] bg-white"
            iframeClassName="h-full min-h-[560px] w-full border-0 bg-white"
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
